import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import NoTicketsImg from 'src/assets/no-tickets.svg';
import Button from 'src/components/ui/Button/Button';
import DateIndicator from 'src/components/ui/DateIndicator/DateIndicator';
import EmptyState from 'src/components/ui/EmptyState/EmptyState';
import PageHeader from 'src/components/ui/PageHeader/PageHeader';
import Table from 'src/components/ui/Table/Table';
import {
  Order,
  TableFilter,
  TableHeader,
  TableQueryParameters,
} from 'src/components/ui/Table/Table.interface';
import TablePagination from 'src/components/ui/Table/TablePagination/TablePagination';
import { paginationDefaultState } from 'src/constants/paginationConstants';
import { emptyTable, ticketFilters } from 'src/constants/ticketConstants';
import { routes } from 'src/routes';
import { getTicketList, setTicketList } from 'src/store/actions/ticketActions';
import { Role } from 'src/store/models/Auth';
import { ListTicket, TicketStatus } from 'src/store/models/Ticket';
import { getTicketSortingDate } from 'src/utils/ticketUtils';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';
import { useQueryParams } from 'src/utils/useQueryParams';

import classes from './Tickets.module.scss';

const Tickets: FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const user = useAppSelector((state) => state.auth.user);
  const ticketList = useAppSelector((state) => state.ticket.ticketList);
  const loading = useLoadingSelector({ actionType: getTicketList.toString() });
  const isService = user?.role === Role.SERVICE;
  const isManufacturer = user?.role === Role.MANUFACTURER;

  const headers: TableHeader<ListTicket>[] = [
    {
      accessor: 'id',
      label: t('tickets:ticket_id'),
      fontWeight: 700,
      component: (ticket) => {
        return ticket?.status === TicketStatus.UNASSIGNED
          ? '/'
          : ticket.ticketId;
      },
    },
    ...(isManufacturer
      ? [
          {
            accessor: 'assignee' as keyof ListTicket,
            label: t('common:assignee'),
            component: (ticket) => (
              <>
                <p>{ticket?.assignee?.title || '/'}</p>
                {(ticket?.status === TicketStatus.TO_DO ||
                  ticket?.status === TicketStatus.IN_PROGRESS) && (
                  <Link
                    to={`${routes.TICKETS}/${ticket?.id}${routes.REASSIGN}`}
                    onClick={(e) => e.stopPropagation()}
                    className={classes.Reassign}
                  >
                    {t('tickets:reassign')}
                  </Link>
                )}
              </>
            ),
          } as TableHeader<ListTicket>,
        ]
      : []),
    {
      accessor: 'owner',
      label: t('tickets:device_owner'),
    },
    {
      accessor: 'address',
      label: t('common:address'),
    },
    {
      accessor: 'unitId',
      label: t('common:device'),
    },
    {
      accessor: 'createdAt',
      label: t('tickets:dates'),
      component: (ticket) => {
        return <DateIndicator ticket={ticket} />;
      },
    },
  ];

  const filterItems = ticketFilters[user?.role];

  const { urlParams, updateURLWithQueryParams } = useQueryParams<
    TableQueryParameters<TicketStatus, ListTicket>
  >(location.search, {
    ...paginationDefaultState,
    s: filterItems?.[0]?.value,
    mode: Order.DESC,
    key: getTicketSortingDate(filterItems?.[0]?.value),
  });

  const onFilterChange = (item: TableFilter<TicketStatus>) => {
    updateURLWithQueryParams(location.pathname, {
      ...urlParams,
      page: 0,
      s: item.value,
      mode: Order.DESC,
      key: getTicketSortingDate(item.value),
    });
  };

  useEffect(() => {
    dispatch(setTicketList({ count: 0, data: [], ctx: null }));
    return () => {
      dispatch(setTicketList({ count: 0, data: [], ctx: null }));
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isManufacturer) {
      dispatch(getTicketList({ params: urlParams }));
    } else {
      dispatch(
        getTicketList({
          manId: user?.manufacturer?.id,
          params: urlParams,
        })
      );
    }
    // eslint-disable-next-line
  }, [location]);

  const onRowClick = (ticket: ListTicket) => {
    if (isService) {
      navigate(`${routes.TICKETS}/${ticket?.id}`);
    } else if (isManufacturer) {
      if (ticket.status === TicketStatus.UNASSIGNED) {
        navigate(`${routes.TICKETS}/${ticket?.id}${routes.ASSIGN}`);
      } else {
        navigate(`${routes.TICKETS}/${ticket?.id}`);
      }
    }
  };

  return (
    <div className={classes.Container}>
      <PageHeader
        title={t('tickets:all_tickets')}
        endNode={
          isService && (
            <Button
              onClick={() => navigate(routes.NEW_TICKET)}
              endIcon={<FontAwesomeIcon icon={faPlus} />}
              className={classes.HeaderButton}
            >
              {t('tickets:create_new')}
            </Button>
          )
        }
      />
      <Table
        headers={headers}
        data={ticketList?.data}
        filterItems={filterItems}
        onFilterChange={onFilterChange}
        search
        loading={loading}
        className={classes.Table}
        emptyTableComponent={
          <EmptyState
            title={
              emptyTable?.[user?.role]?.[urlParams?.s as TicketStatus]?.title
            }
            subtitle={
              emptyTable?.[user?.role]?.[urlParams?.s as TicketStatus]?.subtitle
            }
            icon={NoTicketsImg}
          />
        }
        onRowClick={onRowClick}
        searchPlaceholder={t('common:search_field')}
      />
      <TablePagination
        className={classes.Pagination}
        urlParams={urlParams}
        total={ticketList?.count}
      />
    </div>
  );
};

export default Tickets;
